/**
 * BootstrapGate — the boot guard for the platform bootstrap call. Mounted above
 * everything so `authOrigin`/`loginUrl` are resolved before any route renders.
 *
 * On mount it fetches bootstrap once (guarded by a ref, so StrictMode's
 * double-invoke does not double-fetch).
 *
 *  - pending  → <LoadingSpinner />.
 *  - resolved → setBootstrap(payload), then render children.
 *  - failed   → a blocking error screen: the message, a Retry button re-issuing
 *    the request, and nothing else. A 404 means this host is not registered (an
 *    operator provisioning issue, not a user error) and gets its own message;
 *    any other failure gets the generic one. No composed-host fallback is ever
 *    attempted (hard constraint).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { LoadingSpinner } from './LoadingSpinner';
import { PreAppLayout } from './PreAppLayout';
import { BootstrapError, fetchBootstrap, setBootstrap } from '@/config/bootstrap';
import { useCopy } from '@/hooks/useCopy';

type GateState = 'pending' | 'resolved' | 'failed';

export interface BootstrapGateProps {
  children: ReactNode;
}

export function BootstrapGate({ children }: BootstrapGateProps): ReactNode {
  const [state, setState] = useState<GateState>('pending');
  const [notRegistered, setNotRegistered] = useState(false);
  const started = useRef(false);
  const { t } = useCopy();

  const load = useCallback(() => {
    setState('pending');
    void (async () => {
      try {
        const payload = await fetchBootstrap();
        setBootstrap(payload);
        setState('resolved');
      } catch (err) {
        setNotRegistered(err instanceof BootstrapError && err.status === 404);
        setState('failed');
      }
    })();
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  function retry(): void {
    load();
  }

  if (state === 'resolved') {
    return children;
  }

  if (state === 'failed') {
    const message = notRegistered
      ? t(
          'bootstrap.errorNotRegistered',
          'This host is not registered. Contact your administrator.',
        )
      : t('bootstrap.error', 'Could not load application configuration.');
    return (
      <PreAppLayout>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2,
          }}
        >
          <Typography variant="body1" component="h1" sx={{ color: 'text.secondary' }}>
            {message}
          </Typography>
          <Button variant="outlined" onClick={retry}>
            {t('bootstrap.retry', 'Retry')}
          </Button>
        </Box>
      </PreAppLayout>
    );
  }

  return (
    <PreAppLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <LoadingSpinner />
      </Box>
    </PreAppLayout>
  );
}
